import type { ProcessedImage, ProcessingOptions } from "../types";
import { mimeByFormat, savingsPercent } from "./format";
import { assertReasonableSize, calculateTargetSize, outputFilename, qualityFor } from "./imagePipeline";
import { encodeBmp, encodeTiff } from "./rasterEncoders";

export async function processInBrowserCanvas(
  file: File,
  sourceName: string,
  originalSize: number,
  options: ProcessingOptions,
  onProgress: (progress: number) => void
): Promise<ProcessedImage> {
  onProgress(8);
  const image = await decodeImage(file);
  onProgress(30);

  const target = calculateTargetSize(image.naturalWidth, image.naturalHeight, options);
  assertReasonableSize(target.width, target.height);
  const encoded = await encodeOptimized(image, target.width, target.height, originalSize, options, onProgress);
  onProgress(72);

  onProgress(100);

  return {
    blob: encoded.blob,
    url: URL.createObjectURL(encoded.blob),
    filename: outputFilename(sourceName || file.name, options),
    size: encoded.blob.size,
    width: encoded.width,
    height: encoded.height,
    format: options.format,
    savingsPercent: savingsPercent(originalSize, encoded.blob.size),
    wasLargerThanOriginal: encoded.blob.size > originalSize,
    optimizationApplied: encoded.optimizationApplied
  };
}

async function decodeImage(file: File) {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.decoding = "async";
  image.src = url;

  try {
    await image.decode();
    return image;
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

async function encodeCanvas(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D, options: ProcessingOptions) {
  if (options.format === "bmp" || options.format === "tiff") {
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    return options.format === "bmp" ? encodeBmp(imageData) : encodeTiff(imageData);
  }

  const mimeType = mimeByFormat[options.format];
  const quality = qualityFor(options.format, options.quality);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        if (options.format === "avif") {
          reject(new Error("AVIF export is not available in this browser. Choose WebP, JPEG, or PNG."));
          return;
        }

        reject(new Error(`Could not export ${options.format.toUpperCase()}. Try a smaller image or a different format.`));
      },
      mimeType,
      quality
    );
  });
}

async function encodeOptimized(
  image: HTMLImageElement,
  width: number,
  height: number,
  originalSize: number,
  options: ProcessingOptions,
  onProgress: (progress: number) => void
) {
  const attempts = buildAttempts(width, height, options);
  let best: { blob: Blob; width: number; height: number; optimizationApplied?: string } | undefined;

  try {
    for (let index = 0; index < attempts.length; index += 1) {
      const attempt = attempts[index];
      const rendered = renderToCanvas(image, attempt.width, attempt.height, options);
      const blob = await encodeCanvas(rendered.canvas, rendered.context, { ...options, quality: attempt.quality });
      const candidate = { blob, width: attempt.width, height: attempt.height, optimizationApplied: attempt.label };
      if (!best || candidate.blob.size < best.blob.size) best = candidate;
      if (!options.preventLargerOutput || blob.size <= originalSize || !canOptimizeFormat(options.format)) return candidate;
      onProgress(Math.min(92, 72 + Math.round(((index + 1) / attempts.length) * 18)));
    }

    return best!;
  } finally {
    URL.revokeObjectURL(image.src);
  }
}

function renderToCanvas(image: HTMLImageElement, width: number, height: number, options: ProcessingOptions) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const keepsAlpha = options.format === "png" || options.format === "webp" || options.format === "avif";
  const context = canvas.getContext("2d", { alpha: keepsAlpha });
  if (!context) throw new Error("Your browser could not create a canvas context for this image.");

  if (!keepsAlpha) {
    context.fillStyle = options.jpegBackground || "#ffffff";
    context.fillRect(0, 0, width, height);
  } else {
    context.clearRect(0, 0, width, height);
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, width, height);
  return { canvas, context };
}

function buildAttempts(width: number, height: number, options: ProcessingOptions) {
  if (!canOptimizeFormat(options.format)) return [{ width, height, quality: options.quality, label: undefined }];

  const qualities = Array.from(new Set([options.quality, 76, 68, 60, 52, 44].filter((quality) => quality <= options.quality)));
  const scales = options.autoDownscaleLargeOutput ? [1, 0.75, 0.5] : [1];

  return scales.flatMap((scale) =>
    qualities.map((quality) => ({
      width: Math.max(1, Math.round(width * scale)),
      height: Math.max(1, Math.round(height * scale)),
      quality,
      label: scale < 1 ? `Auto optimized: ${quality}% quality, ${Math.round(scale * 100)}% scale` : quality < options.quality ? `Auto optimized: ${quality}% quality` : undefined
    }))
  );
}

function canOptimizeFormat(format: ProcessingOptions["format"]) {
  return format === "jpeg" || format === "webp" || format === "avif";
}
